import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';

export type ResourceType = 'texture' | 'buffer';

export interface RenderResourceDesc {
  type: ResourceType;
  name: string;
  // Texture dimensions & parameters
  width?: number;
  height?: number;
  format?: string;
  usage?: number;
  // Buffer size & parameter
  size?: number;
  bufferUsage?: number;
}

export interface RenderResource {
  desc: RenderResourceDesc;
  gpuResource: any; // GPUTexture or GPUBuffer
  view?: any;       // GPUTextureView for textures
}

export interface RenderPassNode {
  name: string;
  inputs: string[];  // Names of resources read by this pass
  outputs: string[]; // Names of resources written/modified by this pass
  isGroupable: boolean; // True if this pass can be bundled into a shared active render pass
  execute: (context: {
    device: any;
    commandEncoder: any;
    passEncoder: any; // Active composite GPURenderPassEncoder, if grouped
    resources: Map<string, RenderResource>;
  }) => void;
}

export class WebGPUResourcePool {
  private device: any;
  private textures = new Map<string, Array<{ texture: any; view: any; lastFrameUsed: number }>>();
  private buffers = new Map<string, Array<{ buffer: any; lastFrameUsed: number }>>();
  private currentFrame = 0;

  constructor(device: any) {
    this.device = device;
  }

  public advanceFrame() {
    this.currentFrame++;
    this.garbageCollect();
  }

  public getTexture(desc: RenderResourceDesc): { texture: any; view: any } {
    const key = `${desc.width}x${desc.height}_${desc.format}_${desc.usage}`;
    const pool = this.textures.get(key);

    if (pool && pool.length > 0) {
      const entry = pool.pop()!;
      entry.lastFrameUsed = this.currentFrame;
      return { texture: entry.texture, view: entry.view };
    }

    // Allocate new GPU Texture
    const texture = this.device.createTexture({
      label: `Pool Particle/FX Texture: ${desc.name}`,
      size: [desc.width || 1280, desc.height || 720],
      format: desc.format || 'bgra8unorm',
      usage: desc.usage || (0x02 | 0x10) // RENDER_ATTACHMENT | TEXTURE_BINDING
    });

    const view = texture.createView();

    // Track VRAM allocation heights
    PerformanceTelemetry.vramDetails.texturesAndFB += (desc.width || 1280) * (desc.height || 720) * 4;

    return { texture, view };
  }

  public releaseTexture(desc: RenderResourceDesc, texture: any, view: any) {
    const key = `${desc.width}x${desc.height}_${desc.format}_${desc.usage}`;
    if (!this.textures.has(key)) {
      this.textures.set(key, []);
    }
    this.textures.get(key)!.push({ texture, view, lastFrameUsed: this.currentFrame });
  }

  public getBuffer(desc: RenderResourceDesc): any {
    const key = `${desc.size}_${desc.bufferUsage}`;
    const pool = this.buffers.get(key);

    if (pool && pool.length > 0) {
      const entry = pool.pop()!;
      entry.lastFrameUsed = this.currentFrame;
      return entry.buffer;
    }

    // Allocate new GPU Buffer
    const buffer = this.device.createBuffer({
      label: `Pool Compute/Indirect Buffer: ${desc.name}`,
      size: desc.size || 1024,
      usage: desc.bufferUsage || (0x20 | 0x08) // STORAGE | COPY_DST
    });

    PerformanceTelemetry.vramDetails.storageBuffers += desc.size || 1024;

    return buffer;
  }

  public releaseBuffer(desc: RenderResourceDesc, buffer: any) {
    const key = `${desc.size}_${desc.bufferUsage}`;
    if (!this.buffers.has(key)) {
      this.buffers.set(key, []);
    }
    this.buffers.get(key)!.push({ buffer, lastFrameUsed: this.currentFrame });
  }

  private garbageCollect() {
    // Collect/release resources unused for more than 120 frames (approx. 2 seconds)
    const threshold = 120;
    const oldestAllowedFrame = this.currentFrame - threshold;

    this.textures.forEach((list, key) => {
      const active = list.filter(entry => {
        if (entry.lastFrameUsed < oldestAllowedFrame) {
          entry.texture.destroy();
          PerformanceTelemetry.vramDetails.texturesAndFB -= (entry.texture.width || 1280) * (entry.texture.height || 720) * 4;
          return false;
        }
        return true;
      });
      this.textures.set(key, active);
    });

    this.buffers.forEach((list, key) => {
      const active = list.filter(entry => {
        if (entry.lastFrameUsed < oldestAllowedFrame) {
          entry.buffer.destroy();
          PerformanceTelemetry.vramDetails.storageBuffers -= entry.buffer.size;
          return false;
        }
        return true;
      });
      this.buffers.set(key, active);
    });
  }

  public dispose() {
    this.textures.forEach(list => list.forEach(entry => entry.texture.destroy()));
    this.buffers.forEach(list => list.forEach(entry => entry.buffer.destroy()));
    this.textures.clear();
    this.buffers.clear();
  }
}

export class RenderGraph {
  private device: any;
  private pool: WebGPUResourcePool;
  private nodes: RenderPassNode[] = [];
  private resources = new Map<string, RenderResource>();
  private resourceOwners = new Map<string, string>(); // tracks which node created or wrote to the resource
  private transientDescriptions = new Map<string, RenderResourceDesc>();

  constructor(device: any) {
    this.device = device;
    this.pool = new WebGPUResourcePool(device);
  }

  public clear() {
    this.nodes = [];
    this.transientDescriptions.clear();
    this.resourceOwners.clear();
    // Keep external imported resources but clear transient ones
    const keysToKeep = new Array<string>();
    this.resources.forEach((res, key) => {
      if (res.desc.name.startsWith('imported_')) {
        keysToKeep.push(key);
      }
    });

    const newResources = new Map<string, RenderResource>();
    keysToKeep.forEach(k => {
      newResources.set(k, this.resources.get(k)!);
    });
    this.resources = newResources;
  }

  public importResource(name: string, desc: RenderResourceDesc, gpuResource: any, view?: any) {
    desc.name = `imported_${name}`;
    this.resources.set(name, { desc, gpuResource, view });
  }

  public registerTransientResource(name: string, desc: RenderResourceDesc) {
    desc.name = `transient_${name}`;
    this.transientDescriptions.set(name, desc);
  }

  public addNode(node: RenderPassNode) {
    this.nodes.push(node);
  }

  public execute() {
    // Phase 6.1 & 6.2 — Topological Sort and Lifetime Compile Engine

    // 1. Solve pass execution sequence dependencies (Topological Sort)
    const sortedNodes = this.topologicalSort();

    // 2. Track transient resource lifetime ranges [firstWritePassIdx, lastReadPassIdx]
    const resourceLifetimes = new Map<string, { firstActive: number; lastActive: number }>();
    
    // Scan passes to calculate indices
    for (let i = 0; i < sortedNodes.length; i++) {
      const node = sortedNodes[i];
      const allRefs = [...node.inputs, ...node.outputs];
      
      allRefs.forEach(resName => {
        if (this.transientDescriptions.has(resName)) {
          if (!resourceLifetimes.has(resName)) {
            resourceLifetimes.set(resName, { firstActive: i, lastActive: i });
          } else {
            const life = resourceLifetimes.get(resName)!;
            life.lastActive = i;
          }
        }
      });
    }

    // 3. Setup runtime execution pipelines
    const commandEncoder = this.device.createCommandEncoder({ label: 'RenderGraph unified encoder' });

    let activePassEncoder: any = null;
    let fallbackView: any = null;
    const clearedViews = new Set<any>();

    // Helper functions to manage physical instantiation of transient resources
    const allocateResource = (name: string) => {
      if (this.resources.has(name)) return;
      const desc = this.transientDescriptions.get(name)!;

      if (desc.type === 'texture') {
        const { texture, view } = this.pool.getTexture(desc);
        this.resources.set(name, { desc, gpuResource: texture, view });
      } else {
        const buffer = this.pool.getBuffer(desc);
        this.resources.set(name, { desc, gpuResource: buffer });
      }
    };

    const deallocateResource = (name: string) => {
      const res = this.resources.get(name);
      if (!res) return;

      this.resources.delete(name);
      if (res.desc.type === 'texture') {
        this.pool.releaseTexture(res.desc, res.gpuResource, res.view);
      } else {
        this.pool.releaseBuffer(res.desc, res.gpuResource);
      }
    };

    // 4. Iterate over passes sequentially
    for (let i = 0; i < sortedNodes.length; i++) {
      const node = sortedNodes[i];

      // Allocate transient resources debuting in this pass step
      resourceLifetimes.forEach((life, name) => {
        if (life.firstActive === i) {
          allocateResource(name);
        }
      });

      // Find if we are outputting to standard primary presentation outputs or transient render buffers
      const backbufferRef = node.outputs.find(out => out === 'backbuffer' || out === 'swapchain');
      const targetView = backbufferRef 
        ? this.resources.get(backbufferRef)?.view 
        : (node.outputs.length > 0 ? this.resources.get(node.outputs[0])?.view : null);

      if (node.isGroupable && targetView) {
        // Renderpass bundling (dynamic consolidation of render operations to save bandwidth)
        if (!activePassEncoder || fallbackView !== targetView) {
          if (activePassEncoder) {
            activePassEncoder.end();
          }
          fallbackView = targetView;
          
          const isFirstPassForView = !clearedViews.has(targetView);
          if (isFirstPassForView) {
            clearedViews.add(targetView);
          }

          activePassEncoder = commandEncoder.beginRenderPass({
            label: `Dynamic Pass Consolidation Layer: ${node.name}`,
            colorAttachments: [{
              view: targetView,
              loadOp: isFirstPassForView ? 'clear' : 'load',
              clearValue: { r: 0.0, g: 0.0, b: 0.0, a: 0.0 },
              storeOp: 'store'
            }]
          });
        }
      } else {
        // Group pass termination
        if (activePassEncoder) {
          activePassEncoder.end();
          activePassEncoder = null;
          fallbackView = null;
        }
      }

      // Trigger node business routine
      try {
        node.execute({
          device: this.device,
          commandEncoder,
          passEncoder: activePassEncoder,
          resources: this.resources
        });
      } catch (err) {
        console.error(`Error executing RenderGraph node: ${node.name}`, err);
      }

      // Recycle transient resources that have expired in this pass step
      resourceLifetimes.forEach((life, name) => {
        if (life.lastActive === i) {
          deallocateResource(name);
        }
      });
    }

    // Terminate any trailing active multi-pass encoders
    if (activePassEncoder) {
      activePassEncoder.end();
    }

    // Submit instructions to actual queues
    this.device.queue.submit([commandEncoder.finish()]);

    // Proceed resource pools frame indexes
    this.pool.advanceFrame();
  }

  private topologicalSort(): RenderPassNode[] {
    const sorted: RenderPassNode[] = [];
    const visited = new Set<string>();
    const tempVisited = new Set<string>();

    const visit = (node: RenderPassNode) => {
      const nodeId = node.name;
      if (visited.has(nodeId)) return;
      if (tempVisited.has(nodeId)) {
        // Cyclic recovery logic to guarantee stable running logic
        console.warn('Cyclic reference discovered inside the simulation RenderGraph. Resolving chain.');
        return;
      }

      tempVisited.add(nodeId);

      // Find any passes that produce the inputs needed by this pass
      this.nodes.forEach(candidate => {
        if (candidate.name === node.name) return;
        const producesInput = candidate.outputs.some(out => node.inputs.includes(out));
        if (producesInput) {
          visit(candidate);
        }
      });

      tempVisited.delete(nodeId);
      visited.add(nodeId);
      sorted.push(node);
    };

    this.nodes.forEach(n => visit(n));
    return sorted;
  }

  public dispose() {
    this.pool.dispose();
    this.resources.clear();
    this.nodes = [];
  }
}
